import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { FileTextIcon, MailIcon, PrinterIcon, RotateCcwIcon, UserIcon, XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useLatestLoad } from '../../hooks/useLatestLoad';
import { fetchOrder } from '../adminApi';
import { useAdmin } from '../AdminContext';
import { PageHeader } from '../ui/PageHeader';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { MissingItem } from '../ui/MissingItem';
import { LoadFailed } from '../ui/LoadState';
import { Spinner } from '../../ui/Spinner';
import { btnDestructive, btnSecondary } from '../ui/styles';
import { paymentLabel, received } from '../inbox/inboxRules';
import { RefundForm } from './RefundForm';
import { CancelForm } from './CancelForm';
import { OrderTimeline } from './OrderTimeline';
import { FulfilmentPanel } from './FulfilmentPanel';
import { OrderMarginPanel } from './OrderMarginPanel';
import { creditNumber, printCreditNote, printOrderInvoice, printOrderPackingSlip } from './orderDocuments';
import { channelLabel, orderBadge, orderNumber, refundLabel, vatBreakdown } from './orderLabels';
import { money } from '../../core/format/currency';
import { cancelProblem, paymentMethodLabel, refundableCents } from '../../core/rules/revenue';
import { formatStreet } from '../../core/rules/contact';
import { trackingUrl } from '../../core/rules/tracking';
import { dateOf, dayShort } from '../../core/format/dates';
import { groupByBundle } from '../../core/rules/orderBundles';
import type { AdminOrder } from '../../core/types/adminRequests';
import type { RequestOptions } from '../../core/types/backend';
type Action = 'refund' | 'cancel' | null;

/**
 * Eén bestelling: wat erin zat (met de btw zoals verkocht), het adres, de betaling, de tijdlijn en
 * de terugbetalingen. Terugbetalen en annuleren wachten altijd op de server (transactiescherm).
 */
export function OrderDetail() {
  const {
    id = ''
  } = useParams();
  // Vanuit de lijst: terug met dezelfde filters. Vanuit de inbox: terug naar de inbox.
  const {
    state
  } = useLocation() as {
    state: {
      from?: string;
      back?: {
        to: string;
        label: string;
      };
    } | null;
  };
  const back = state?.back ?? {
    to: `/be/bestellingen${state?.from ?? ''}`,
    label: 'Alle bestellingen'
  };
  const load = useCallback((opts: RequestOptions) => fetchOrder(id, opts), [id]);
  const {
    data: order,
    setData,
    status,
    reload,
    refresh
  } = useLatestLoad(load);
  const [action, setAction] = useState<Action>(null);
  if (status === 'loading' && !order) return <div role="status" className="flex items-center gap-2 py-16 text-[14px] text-muted" data-id="element-3525" data-mp="1tm11qu58xxli">
        <Spinner data-id="element-3526" data-mp="1pkpcwqu75ged" /> Bestelling laden…
      </div>;
  if (status === 'error' && !order) return <LoadFailed title="Deze bestelling kon niet laden" onRetry={reload} data-id="element-3527" data-mp="3o021dh8meomn" />;
  if (!order) return <MissingItem title="Bestelling niet gevonden" back={back} data-id="element-3528" data-mp="3jyqhlscfgxmd" />;
  const done = (next: AdminOrder, message: string) => {
    setData(next);
    setAction(null);
    toast.success(message);
  };
  return <OrderView order={order} back={back} action={action} setAction={setAction} onRefunded={next => done(next, 'Terugbetaald. De klant krijgt een mail van Mollie.')} onCancelled={next => done(next, 'Bestelling geannuleerd')} onFailed={refresh} onFulfilled={refresh} data-id="element-3529" data-mp="1apsho7tc63lk" />;
}
type ViewProps = {
  order: AdminOrder;
  back: {
    to: string;
    label: string;
  };
  action: Action;
  setAction: (a: Action) => void;
  onRefunded: (o: AdminOrder) => void;
  onCancelled: (o: AdminOrder) => void;
  onFailed: () => void;
  onFulfilled: () => void;
};
function OrderView({
  order: o,
  back,
  action,
  setAction,
  onRefunded,
  onCancelled,
  onFailed,
  onFulfilled
}: ViewProps) {
  const {
    site,
    index
  } = useAdmin();
  const badge = orderBadge(o);
  const paid = o.paymentStatus === 'betaald';
  const left = refundableCents(o);
  const canRefund = paid && left > 0 && !o.cancelledAt;
  const canCancel = cancelProblem(o) === null;
  const vat = vatBreakdown(o);
  const a = o.address;

  // Het formulier opent onder de regels; op de telefoon valt dat buiten beeld.
  const formRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (action) formRef.current?.scrollIntoView({
      block: 'nearest'
    });
  }, [action]);
  const invoice = async () => {
    if (!(await printOrderInvoice(o, site.salon, dayShort(dateOf(o.placedAt))))) toast.error('Factuur openen lukte niet', {
      description: 'Probeer het opnieuw of gebruik een andere browser.'
    });
  };
  const slip = async () => {
    if (!(await printOrderPackingSlip(o, index.productById, site.salon, id => index.routineById(id)?.name))) toast.error('Pakbon afdrukken lukte niet', {
      description: 'Probeer het opnieuw of gebruik een andere browser.'
    });
  };
  return <div data-id="element-3530" data-mp="3cfrbfc0kiosv">
      <PageHeader title={o.number ? `Bestelling ${o.number}` : 'Bestelling zonder nummer'} description={`${o.customerName || o.email} · ${received(o.placedAt)} · ${channelLabel[o.channel]} · ${money(o.totalCents)}`} back={back} actions={<>
            <Badge tone={badge.tone} data-id="element-3532" data-mp="3w0hsfuclkt8m">{badge.label}</Badge>
            {paid && <button type="button" onClick={() => void invoice()} className={btnSecondary} data-id="element-3533" data-mp="3i072qun0xkp1">
                <FileTextIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3534" data-mp="3m1slgwm8ot75" />
                Factuur
              </button>}
            {paid && !o.cancelledAt && <button type="button" onClick={() => void slip()} className={btnSecondary} data-id="element-3535" data-mp="3d2f83suzvkh5">
                <PrinterIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3536" data-mp="2ydrfnvgg3qvf" />
                Pakbon
              </button>}
          </>} data-id="element-3531" data-mp="3cfhj3hs1w5k0" />

      {/* Art. 35a Wet OB: het btw-id hoort op elke factuur. Leeg = de factuur mist het, dus zeg het hier. */}
      {paid && !site.salon.vatId.trim() && <p role="note" className="mb-6 rounded-lg bg-sand px-4 py-3 text-[13px] text-ink" data-id="element-3537" data-mp="2ub4kploh9pu0">
          Je btw-id staat nog niet in Instellingen › Salon & contact. Zonder btw-id is deze factuur niet volledig.{' '}
          <Link to="/be/instellingen?tab=salon" className="underline underline-offset-4" data-id="element-3538" data-mp="v3efurib6kjh">
            Btw-id invullen
          </Link>
        </p>}

      <div className="grid gap-6 lg:grid-cols-3" data-id="element-3539" data-mp="m4kj914z9u5x">
        <div className="space-y-6 lg:col-span-2" data-id="element-3540" data-mp="m1y34d40u6c3">
          <FulfilmentPanel order={o} onChanged={onFulfilled} data-id="element-3541" data-mp="1iujsyywv6yq3" />
          <Panel title="Wat er besteld is" flush data-id="element-3542" data-mp="1irxm71hssn8x">
            <table className="w-full text-[14px]" data-id="element-3543" data-mp="1dw9uoffufi09">
              <caption className="sr-only" data-id="element-3544" data-mp="19tlnsdaowl5x">Bestelregels met btw</caption>
              <thead className="text-[12px] text-muted" data-id="element-3545" data-mp="19rsgqww5zyxe">
                <tr className="border-b border-line" data-id="element-3546" data-mp="14vbp3sprb2fk">
                  <th scope="col" className="px-4 py-2 text-left font-normal sm:px-5" data-id="element-3547" data-mp="14wv7ppyvxpki">Product</th>
                  <th scope="col" className="px-2 py-2 text-right font-normal" data-id="element-3548" data-mp="3uhhs2zxnt2lc">Aantal</th>
                  <th scope="col" className="hidden px-2 py-2 text-right font-normal sm:table-cell" data-id="element-3549" data-mp="3pmudptwddokh">Btw</th>
                  <th scope="col" className="px-4 py-2 text-right font-normal sm:px-5" data-id="element-3550" data-mp="250vzuytp162h">Bedrag</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line" data-id="element-3551" data-mp="2e359lmh50vet">
                {/* Een protocol als bundel onder één kopje: dan pakt Sarah het als set in. */}
                {groupByBundle(o.lines, l => l.bundleRoutineId).flatMap(g => [...(g.routineId ? [<tr key={`bundle-${g.routineId}`} className="bg-sand/60" data-id="element-3552" data-mp="2sri92tffowe2">
                          <th scope="rowgroup" colSpan={4} className="px-4 py-2 text-left text-[12px] font-normal text-clay sm:px-5" data-id="element-3553" data-mp="2xrnuhrv41mnp">
                            Protocol · {index.routineById(g.routineId)?.name ?? g.routineId} · {g.lines.length} producten
                          </th>
                        </tr>] : []), ...g.lines.map(l => <tr key={l.productId} data-id="element-3554" data-mp="oi6iv8cyp0hb">
                      <td className={`py-3 pr-4 text-ink sm:pr-5 ${g.routineId ? 'pl-7 sm:pl-8' : 'pl-4 sm:pl-5'}`} data-id="element-3555" data-mp="oh5874zuyg04">
                        {l.name}
                        <span className="block text-[12px] text-muted sm:hidden" data-id="element-3556" data-mp="xarxqddh2n0v">btw {l.vatRate}%</span>
                      </td>
                      <td className="px-2 py-3 text-right tabular-nums" data-id="element-3557" data-mp="tapwu6fk9xmn">{l.qty}</td>
                      <td className="hidden px-2 py-3 text-right tabular-nums text-muted sm:table-cell" data-id="element-3558" data-mp="5j2eicwd7uvz">{l.vatRate}%</td>
                      <td className="px-4 py-3 text-right tabular-nums text-ink sm:px-5" data-id="element-3559" data-mp="m2fun1uhuew">{money(l.lineTotalCents)}</td>
                    </tr>)])}
              </tbody>
            </table>
            <dl className="space-y-1.5 border-t border-line px-4 py-4 text-[13px] sm:px-5" data-id="element-3560" data-mp="9k4gv2lkwn26">
              <div className="flex justify-between gap-4" data-id="element-3561" data-mp="1pxuky9cp7bbt">
                <dt className="text-muted" data-id="element-3562" data-mp="1l1duv9lxumw3">Verzending (btw {o.shippingVatRate}%)</dt>
                <dd className="tabular-nums text-ink" data-id="element-3563" data-mp="1th3gaj20p0uc">{o.shippingCents ? money(o.shippingCents) : 'Gratis'}</dd>
              </div>
              {vat.map(v => <div key={v.vatRate} className="flex justify-between gap-4" data-id="element-3564" data-mp="1xh3uc2l7fa0v">
                  <dt className="text-muted" data-id="element-3565" data-mp="1jl056np6trv9">Waarvan btw {v.vatRate}%</dt>
                  <dd className="tabular-nums text-muted" data-id="element-3566" data-mp="1ojl4mkwm87in">{money(v.vatCents)}</dd>
                </div>)}
              <div className="flex justify-between gap-4 pt-1.5 text-[15px]" data-id="element-3567" data-mp="2c8mi4mkb28us">
                <dt className="text-ink" data-id="element-3568" data-mp="2h65v7j59ofl5">Totaal</dt>
                <dd className="tabular-nums text-ink" data-id="element-3569" data-mp="2h90gte00o6g5">{money(o.totalCents)}</dd>
              </div>
              {o.voucherCents > 0 && <div className="flex justify-between gap-4" data-id="element-3570" data-mp="27gma06u309n4">
                  <dt className="text-muted" data-id="element-3571" data-mp="27f1hsnv8p2sf">Betaald met cadeaubon (gaat niet via Mollie terug)</dt>
                  <dd className="shrink-0 tabular-nums text-ink" data-id="element-3572" data-mp="2v55myzo2talj">{money(o.voucherCents)}</dd>
                </div>}
              {o.refunds.map(r => <div key={r.id} className="flex justify-between gap-4" data-id="element-3573" data-mp="1s20nghs8ngb8">
                  <dt className="min-w-0 text-muted" data-id="element-3574" data-mp="1wziv84uirphn">
                    {refundLabel(r)} {received(r.at)} · <span className="text-ink" data-id="element-3575" data-mp="1j1lvizkrr2yr">{r.reason}</span>
                    {r.restocked && <span data-id="element-3576" data-mp="1nznhbs9kxgdx"> · terug op voorraad</span>}
                    {r.voucherRestoredCents ? <span data-id="element-3577" data-mp="1o1qgb1spmjus"> · {money(r.voucherRestoredCents)} terug op cadeaubon</span> : null}
                    <button type="button" onClick={() => void printCreditNote(o, r, site.salon).then(ok => {
                  if (!ok) toast.error('Creditnota openen lukte niet', {
                    description: 'Probeer het opnieuw of gebruik een andere browser.'
                  });
                })} className="ml-2 text-ink underline decoration-line underline-offset-4 hover:decoration-ink" data-id="element-3578" data-mp="1996eq5l7kkfg">
                      Creditnota {creditNumber(o, r)}
                    </button>
                  </dt>
                  <dd className="shrink-0 tabular-nums text-clay" data-id="element-3579" data-mp="6qlgilxwcy68">−{money(r.amountCents)}</dd>
                </div>)}
            </dl>
            {(o.gift || o.code) && <p className="border-t border-line px-4 py-3 text-[13px] text-muted sm:px-5" data-id="element-3580" data-mp="goj4r9frauyu">
                {o.gift && <>
                    Cadeau erbij: <span className="text-ink" data-id="element-3581" data-mp="umpug2ajxonf">{o.gift}</span>
                  </>}
                {o.gift && o.code && ' · '}
                {o.code && <>
                    Code: <span className="text-ink" data-id="element-3582" data-mp="14dkklhqx58e5">{o.code}</span>
                  </>}
              </p>}
          </Panel>

          <OrderMarginPanel order={o} data-id="element-3583" data-mp="1dein1vlpl7um" />

          <div ref={formRef} className="scroll-mb-24" data-id="element-3584" data-mp="1dflf46urpgc4">
            {action === 'refund' ? <RefundForm order={o} onDone={onRefunded} onFailed={onFailed} onClose={() => setAction(null)} data-id="element-3585" data-mp="19ehy3h0zhe9b" /> : action === 'cancel' ? <CancelForm order={o} onDone={onCancelled} onFailed={onFailed} onClose={() => setAction(null)} data-id="element-3586" data-mp="1lfhmoytg4ohy" /> : (canRefund || canCancel) && <div className="flex flex-wrap items-center gap-2" data-id="element-3587" data-mp="1qd9m0dwf7cw6">
                  {canRefund && <button type="button" onClick={() => setAction('refund')} className={btnSecondary} data-id="element-3588" data-mp="1v7dytdsdaf56">
                      <RotateCcwIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3589" data-mp="1z98x09f04ceq" />
                      Terugbetalen
                    </button>}
                  {canCancel && <button type="button" onClick={() => setAction('cancel')} className={`${btnDestructive} sm:ml-auto`} data-id="element-3590" data-mp="2e45ab11o3pyo">
                      <XIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3591" data-mp="ert54ff17al4" />
                      Bestelling annuleren
                    </button>}
                </div>}
          </div>
          {!canCancel && !o.cancelledAt && paid && o.fulfilment !== 'nieuw' && action === null && <p className="-mt-3 text-[12px] text-muted" data-id="element-3592" data-mp="2s8e4knr827fr">Al ingepakt of verzonden: annuleren kan niet meer, terugbetalen wel.</p>}
        </div>

        <div className="space-y-6" data-id="element-3593" data-mp="2j9sco1zv1g2b">
          <Panel title="Klant" data-id="element-3594" data-mp="2jak6b79wd3py">
            <p className="text-[14px] text-ink" data-id="element-3595" data-mp="3fz9qj48zy8f3">{o.customerName || '—'}</p>
            <p className="break-all text-[13px] text-muted" data-id="element-3596" data-mp="3fygq0l0msf5e">{o.email}</p>
            <address className="mt-3 text-[13px] not-italic leading-snug text-ink" data-id="element-3597" data-mp="3byzn9zcl9w3x">
              {`${a.firstName} ${a.lastName}`.trim() || o.customerName}
              <br data-id="element-3598" data-mp="31zqw1e93yvl9" />
              {formatStreet(a)}
              <br data-id="element-3599" data-mp="31yyyvyzv4psv" />
              {a.postcode} {a.city}
              {a.country === 'BE' && ' · België'}
            </address>
            {o.skinNote && <p className="mt-3 rounded-md bg-blush/50 px-3 py-2 text-[13px] text-ink" data-id="element-3600" data-mp="1qjshwvz2pjd6">
                <span className="block text-[12px] text-muted" data-id="element-3601" data-mp="3eh9shmwh18ei">Notitie voor jou</span>“{o.skinNote}”
              </p>}
            <div className="mt-4 flex flex-wrap gap-2" data-id="element-3602" data-mp="3j8rrw6iuj0xd">
              {o.customerId ? <Link to={`/be/klanten/${o.customerId}?tab=bestellingen`} className={btnSecondary} data-id="element-3603" data-mp="35h4m9g30kg1x">
                  <UserIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3604" data-mp="3aemsgypnch68" />
                  Dossier
                </Link> : <span className="text-[12px] text-muted" data-id="element-3605" data-mp="1xs40o9lizbo">Gastbestelling</span>}
              <a href={`mailto:${o.email}?subject=${encodeURIComponent(`Je bestelling ${orderNumber(o)}`)}`} className={btnSecondary} data-id="element-3606" data-mp="5z3sgqgqheia">
                <MailIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3607" data-mp="3t5easprm3hwr" />
                Mail
              </a>
            </div>
          </Panel>

          <Panel title="Betaling" data-id="element-3608" data-mp="2ctqvy0ttmiqs">
            <dl className="space-y-1.5 text-[13px]" data-id="element-3609" data-mp="2hra8mwavengm">
              <div className="flex justify-between gap-4" data-id="element-3610" data-mp="2hs21w0gzw93q">
                <dt className="text-muted" data-id="element-3611" data-mp="2a6gda1u48e5b">Status</dt>
                <dd className="text-ink" data-id="element-3612" data-mp="2a92ocbg7tbsa">{paymentLabel[o.paymentStatus]}</dd>
              </div>
              <div className="flex justify-between gap-4" data-id="element-3613" data-mp="25ahl035o2bzm">
                <dt className="text-muted" data-id="element-3614" data-mp="2j6mqdmlwjc3y">Methode</dt>
                <dd className="text-ink" data-id="element-3615" data-mp="2e8thumx4m60i">{paymentMethodLabel[o.paymentMethod]}</dd>
              </div>
              {o.refundedCents > 0 && <div className="flex justify-between gap-4" data-id="element-3616" data-mp="1qk1yml1ikxzv">
                  <dt className="text-muted" data-id="element-3617" data-mp="1mhx8hwa9yjhz">Nog terug te betalen</dt>
                  <dd className="tabular-nums text-ink" data-id="element-3618" data-mp="1mfkiqwy9z4ud">{money(left)}</dd>
                </div>}
              {o.tracking && <div className="flex justify-between gap-4" data-id="element-3619" data-mp="1vi3gx3r48taa">
                  <dt className="text-muted" data-id="element-3620" data-mp="1vgikt9p5l2ab">Track & trace</dt>
                  <dd data-id="element-3621" data-mp="17rg4c5699fhe">
                    <a href={trackingUrl(o.tracking)} target="_blank" rel="noopener noreferrer" className="text-ink underline underline-offset-2" data-id="element-3622" data-mp="17qob3104rtua">
                      {o.tracking.carrier} {o.tracking.code}
                      <span className="sr-only" data-id="element-3623" data-mp="32u33tr4qyfv8"> (opent in een nieuw tabblad)</span>
                    </a>
                  </dd>
                </div>}
            </dl>
          </Panel>

          <Panel title="Tijdlijn" data-id="element-3624" data-mp="3co35tk4qf4zq">
            <OrderTimeline events={o.timeline} data-id="element-3625" data-mp="3hl3bvv1l07qu" />
          </Panel>
        </div>
      </div>
    </div>;
}